import React, { Component } from "react";
import { BASE_URL } from "../common/common";

const API_URL = BASE_URL;
const MAX_IMAGE_SIZE = 3 * 1024 * 1024; // 3 MB

const imgUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http")) return `${path}?ngrok-skip-browser-warning=true`;
  const cleanPath = path.startsWith("/") ? path.slice(1) : path;
  return `${BASE_URL}/${cleanPath}?ngrok-skip-browser-warning=true`;
};

class Clients extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeTab: "banner",

      // Banner
      bannerView: "list",
      bannerList: [],
      bannerListLoading: false,
      bannerTitle: "Clients",
      bannerImagePreview: null,
      bannerFile: null,
      bannerSaving: false,
      editingBanner: null, // ← holds banner being edited

      // Clients
      clientView: "list",
      clientList: [],
      clientListLoading: false,
      editingClient: null, // ← holds client being edited

      clientForm: {
        logo_name: "",
        imagePreview: null,
        imageFile: null,
        saving: false,
      },
      successMessage: "",
      errorMessage: "",
    };
  }

  componentDidMount() {
    this.fetchBannerList();
    this.fetchClientList();
  }

  showSuccess = (msg) => {
    this.setState({ successMessage: msg, errorMessage: "" });
    setTimeout(() => this.setState({ successMessage: "" }), 3000);
  };

  showError = (msg) => {
    this.setState({ errorMessage: msg, successMessage: "" });
    setTimeout(() => this.setState({ errorMessage: "" }), 4000);
  };

  // ════════════════════════════════
  //  BANNER
  // ════════════════════════════════

  fetchBannerList = async () => {
    this.setState({ bannerListLoading: true });
    try {
      const res = await fetch(`${API_URL}/api/banners/getBanners`, {
        method: "GET",
        headers: { "ngrok-skip-browser-warning": "true" },
      });
      const json = await res.json();
      if (json.status) {
        const filtered = json.data.filter(
          (b) => b.Page_Name?.toLowerCase() === "clients",
        );
        this.setState({ bannerList: filtered, bannerListLoading: false });
      } else {
        this.setState({ bannerListLoading: false });
        this.showError("Failed to load banners.");
      }
    } catch (err) {
      this.setState({ bannerListLoading: false });
      this.showError("Cannot connect to API server.");
    }
  };

  // Open blank add form
  openBannerForm = () => {
    this.setState({
      bannerView: "form",
      editingBanner: null,
      bannerTitle: "Clients",
      bannerImagePreview: null,
      bannerFile: null,
    });
  };

  // Open edit form pre-filled
  openEditBanner = (banner) => {
    this.setState({
      bannerView: "form",
      editingBanner: banner,
      bannerTitle: banner.Page_Name || "Clients",
      bannerImagePreview: null,
      bannerFile: null,
    });
  };

  goToBannerList = () => {
    this.setState({ bannerView: "list", editingBanner: null });
    this.fetchBannerList();
  };

  handleBannerImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_IMAGE_SIZE) {
      this.showError("Image size must be less than 3 MB.");
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () =>
      this.setState({ bannerImagePreview: reader.result, bannerFile: file });
    reader.readAsDataURL(file);
  };

  removeBannerImage = () =>
    this.setState({ bannerImagePreview: null, bannerFile: null });

  saveBanner = async () => {
    const { bannerTitle, bannerFile, editingBanner } = this.state;
    if (!editingBanner && !bannerFile) {
      return this.showError("Banner image is required.");
    }
    if (editingBanner && !bannerFile && !editingBanner.imageUrl) {
      return this.showError("Banner image is required.");
    }
    this.setState({ bannerSaving: true });
    try {
      const formData = new FormData();
      formData.append("page_Name", bannerTitle);
      if (bannerFile) formData.append("files", bannerFile);
      if (editingBanner) formData.append("BannerID", editingBanner.Banner_id);

      const url = editingBanner
        ? `${API_URL}/api/banners/updateBanner`
        : `${API_URL}/api/banners/addBanner`;

      const res = await fetch(url, { method: "POST", body: formData });
      const json = await res.json();
      this.setState({ bannerSaving: false });
      if (json.status) {
        this.showSuccess(editingBanner ? "Banner updated!" : "Banner saved!");
        setTimeout(() => this.goToBannerList(), 1000);
      } else {
        this.showError(json.message || "Save failed.");
      }
    } catch (err) {
      this.setState({ bannerSaving: false });
      this.showError("Cannot connect to API server.");
    }
  };

  deleteBanner = async (bannerId) => {
    if (!window.confirm("Are you sure you want to delete this banner?")) return;
    try {
      const res = await fetch(`${API_URL}/api/banners/deleteBanner`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: bannerId }),
      });
      const json = await res.json();
      if (json.status) {
        this.showSuccess("Banner deleted.");
        this.fetchBannerList();
      } else {
        this.showError(json.message || "Delete failed.");
      }
    } catch (err) {
      this.showError("Cannot connect to API server.");
    }
  };

  // ════════════════════════════════
  //  CLIENTS
  // ════════════════════════════════

  fetchClientList = async () => {
    this.setState({ clientListLoading: true });
    try {
      const res = await fetch(`${API_URL}/api/clientlogo/getClientLogo`, {
        method: "GET",
        headers: { "ngrok-skip-browser-warning": "true" },
      });
      const json = await res.json();
      if (json.status) {
        this.setState({
          clientList: json.data.data[0],
          clientListLoading: false,
        });
      } else {
        this.setState({ clientListLoading: false });
        this.showError("Failed to load clients.");
      }
    } catch (err) {
      this.setState({ clientListLoading: false });
      this.showError("Cannot connect to API server.");
    }
  };

  // Open blank add form
  openClientForm = () => {
    this.setState({
      clientView: "form",
      editingClient: null,
      clientForm: {
        logo_name: "",
        imagePreview: null,
        imageFile: null,
        saving: false,
      },
    });
  };
  // Open edit form pre-filled
  openEditClient = (client) => {
    this.setState({
      clientView: "form",
      editingClient: client,
      clientForm: {
        logo_name: client.logo_name || "",
        imagePreview: client.logo_url,
        imageFile: client.logo_url,
        saving: false,
      },
    });
  };

  goToClientList = () => {
    this.setState({ clientView: "list", editingClient: null });
    this.fetchClientList();
  };

  updateClientForm = (field, value) => {
    this.setState((prev) => ({
      clientForm: { ...prev.clientForm, [field]: value },
    }));
  };

  handleClientImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_IMAGE_SIZE) {
      this.showError("Image size must be less than 3 MB.");
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      this.setState((prev) => ({
        clientForm: {
          ...prev.clientForm,
          imagePreview: reader.result,
          imageFile: file,
        },
      }));
    };
    reader.readAsDataURL(file);
  };

  removeClientImage = () => {
    this.setState((prev) => ({
      clientForm: { ...prev.clientForm, imagePreview: null, imageFile: null },
    }));
  };

  saveClient = async () => {
    const { clientForm, editingClient } = this.state;

    if (!clientForm.logo_name) {
      this.showError("Logo name is required");
      return;
    }
    if (!clientForm.imageFile) {
      this.showError("Logo is required");
      return;
    }

    this.setState((prev) => ({
      clientForm: { ...prev.clientForm, saving: true },
    }));

    try {
      const formData = new FormData();
      formData.append("page_Name", "Clients");
      formData.append("logoName", clientForm.logo_name);

      if (clientForm.imageFile) {
        formData.append("image", clientForm.imageFile);
      }

      if (editingClient) {
        formData.append("ClientLogoID", editingClient.logo_id);
      }

      const url = editingClient
        ? `${API_URL}/api/clientlogo/updateClientLogo`
        : `${API_URL}/api/clientlogo/addClientLogo`;

      const res = await fetch(url, { method: "POST", body: formData });
      const json = await res.json();

      this.setState((prev) => ({
        clientForm: { ...prev.clientForm, saving: false },
      }));

      if (json.status) {
        this.showSuccess(
          `${clientForm.logo_name} ${editingClient ? "updated" : "added"} successfully!`,
        );
        setTimeout(() => this.goToClientList(), 1000);
      } else {
        this.showError(json.message || "Save failed.");
      }
    } catch (err) {
      this.setState((prev) => ({
        clientForm: { ...prev.clientForm, saving: false },
      }));
      this.showError("Cannot connect to API server.");
    }
  };
  deleteClient = async (clientId) => {
    if (!window.confirm("Are you sure you want to delete this client?")) return;
    try {
      const res = await fetch(`${API_URL}/api/clientlogo/deleteClientLogo`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: clientId }),
      });
      const json = await res.json();
      if (json.message == "Client logo deleted successfully") {
        this.showSuccess("Client deleted.");
        this.fetchClientList();
      } else {
        this.showError(json.message || "Delete failed.");
      }
    } catch (err) {
      this.showError("Cannot connect to API server.");
    }
  };

  // ════════════════════════════════
  //  RENDER
  // ════════════════════════════════
  render() {
    const {
      activeTab,
      bannerView,
      bannerList,
      bannerListLoading,
      bannerTitle,
      bannerImagePreview,
      bannerSaving,
      editingBanner,
      clientView,
      clientList,
      clientListLoading,
      clientForm,
      editingClient,
      successMessage,
      errorMessage,
    } = this.state;

    return (
      <div>
        <div className="page-header">
          <h2>Clients Page Manager</h2>
          <p>Manage banner and client logos for the Clients page.</p>
        </div>

        {successMessage && <div style={s.success}>{successMessage}</div>}
        {errorMessage && <div style={s.error}>{errorMessage}</div>}

        {/* Tabs */}
        <div className="rsp-tab-row" style={s.tabRow}>
          {["banner", "clients"].map((tab) => (
            <button
              key={tab}
              style={activeTab === tab ? s.tabActive : s.tab}
              onClick={() => this.setState({ activeTab: tab })}
            >
              {tab === "banner" ? "Banner" : "Clients"}
            </button>
          ))}
        </div>

        {/* ══ BANNER TAB ══ */}
        {activeTab === "banner" && (
          <div>
            {/* Banner List */}
            {bannerView === "list" && (
              <div>
                <div className="rsp-list-top" style={s.listTopBar}>
                  <span style={s.countText}>
                    {bannerListLoading
                      ? "Loading..."
                      : `${bannerList.length} banner${bannerList.length !== 1 ? "s" : ""} found`}
                  </span>
                  {bannerList.length === 0 && (
                    <button
                      className="rsp-btn-save" style={s.btnSave}
                      onClick={() => this.openBannerForm()}
                    >
                      + Add Banner
                    </button>
                  )}
                </div>

                {bannerListLoading && (
                  <div style={s.emptyBox}>
                    <p style={s.emptyText}>Loading banners...</p>
                  </div>
                )}

                {!bannerListLoading && bannerList.length === 0 && (
                  <div style={s.emptyBox}>
                    <p style={{ ...s.emptyText, marginBottom: "12px" }}>
                      No banners found.
                    </p>
                    <button className="rsp-btn-save" style={s.btnSave} onClick={this.openBannerForm}>
                      + Add First Banner
                    </button>
                  </div>
                )}

                {!bannerListLoading &&
                  bannerList.map((banner, idx) => (
                    <div key={banner.Banner_id || idx} className="rsp-list-card" style={s.listCard}>
                      {banner.imageUrl ? (
                        <img
                          src={imgUrl(banner.imageUrl)}
                          alt="banner"
                          className="rsp-thumb-wide" style={s.thumbImgWide}
                        />
                      ) : (
                        <div style={s.thumbPlaceholder}>No Image</div>
                      )}

                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            marginBottom: "4px",
                          }}
                        >
                          <strong style={s.itemTitle}>
                            {banner.Page_Name || "—"}
                          </strong>
                          <span style={s.idBadge}>ID: {banner.Banner_id}</span>
                        </div>
                        <p style={s.dateText}>
                          Added:{" "}
                          {new Date(banner.created_at).toLocaleDateString(
                            "en-IN",
                            { day: "2-digit", month: "short", year: "numeric" },
                          )}
                        </p>
                      </div>

                      {/* Edit / Delete */}
                      <div className="rsp-action-btns" style={s.actionBtns}>
                        <button
                          className="rsp-btn-edit" style={s.btnEdit}
                          onClick={() => this.openEditBanner(banner)}
                        >
                          Edit
                        </button>
                        <button
                          className="rsp-btn-delete" style={s.btnDelete}
                          onClick={() => this.deleteBanner(banner.Banner_id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            )}

            {/* Banner Form */}
            {bannerView === "form" && (
              <div>
                <div className="rsp-form-top" style={s.formTopBar}>
                  <button className="rsp-btn-back" style={s.btnBack} onClick={this.goToBannerList}>
                    Back to List
                  </button>
                  <h3 style={s.formTitle}>
                    {editingBanner
                      ? `Edit Banner — ID ${editingBanner.Banner_id}`
                      : "Add New Banner"}
                  </h3>
                </div>

                <div className="card">
                  <div className="rsp-form-group" style={s.formGroup}>
                    <label style={s.label}>Banner Image *</label>

                    {editingBanner &&
                      !bannerImagePreview &&
                      editingBanner.imageUrl && (
                        <div style={{ marginBottom: "10px" }}>
                          <p
                            style={{
                              fontSize: "12px",
                              color: "#888",
                              marginBottom: "6px",
                            }}
                          >
                            Current image:
                          </p>
                          <img
                            src={imgUrl(editingBanner.imageUrl)}
                            alt="current"
                            style={{
                              width: "200px",
                              height: "70px",
                              objectFit: "cover",
                              borderRadius: "6px",
                              border: "1px solid #e8e8e8",
                            }}
                          />
                        </div>
                      )}

                    {bannerImagePreview ? (
                      <div className="rsp-preview-box" style={s.previewBox}>
                        <img
                          src={bannerImagePreview}
                          alt="Preview"
                          style={s.previewImg}
                        />
                        <div style={s.overlay}>
                          <button
                            className="rsp-btn-delete" style={s.btnDelete}
                            onClick={this.removeBannerImage}
                          >
                            Remove
                          </button>
                          <label className="rsp-btn-dark" style={s.btnDark}>
                            Change
                            <input
                              type="file"
                              accept="image/*"
                              onChange={this.handleBannerImage}
                              style={{ display: "none" }}
                            />
                          </label>
                        </div>
                      </div>
                    ) : (
                      <label className="rsp-upload-box" style={s.uploadBox}>
                        <p style={s.uploadText}>
                          {editingBanner
                            ? "Click to replace banner image"
                            : "Click to choose banner image"}
                        </p>
                        <p style={s.uploadHint}>
                          JPG, PNG, WEBP — Max 5MB — 1920x600px recommended
                        </p>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={this.handleBannerImage}
                          style={{ display: "none" }}
                        />
                      </label>
                    )}
                  </div>

                  <div className="rsp-form-group" style={s.formGroup}>
                    <label style={s.label}>Banner Title</label>
                    <input
                      disabled
                      type="text"
                      value={bannerTitle}
                      onChange={(e) =>
                        this.setState({ bannerTitle: e.target.value })
                      }
                      placeholder="e.g. Our Clients"
                      className="rsp-input" style={s.input}
                    />
                  </div>

                  <div className="rsp-form-actions" style={s.formActions}>
                    <button className="rsp-btn-back" style={s.btnBack} onClick={this.goToBannerList}>
                      Cancel
                    </button>
                    <button
                      className="rsp-btn-save" style={s.btnSave}
                      onClick={this.saveBanner}
                      disabled={bannerSaving}
                    >
                      {bannerSaving
                        ? "Saving..."
                        : editingBanner
                          ? "Update Banner"
                          : "Save Banner"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══ CLIENTS TAB ══ */}
        {activeTab === "clients" && (
          <div>
            {/* Client List */}
            {clientView === "list" && (
              <div>
                <div className="rsp-list-top" style={s.listTopBar}>
                  <span style={s.countText}>
                    {clientListLoading
                      ? "Loading..."
                      : `${clientList.length} client${clientList.length !== 1 ? "s" : ""} found`}
                  </span>
                  <button className="rsp-btn-save" style={s.btnSave} onClick={this.openClientForm}>
                    + Add Client
                  </button>
                </div>

                {!clientListLoading && clientList.length > 0 && (
                  <div className="grid-client-responsive">
                    {clientList.map((client, idx) => (
                      <div key={client.logo_id || idx} style={s.clientCard}>
                        {client.logo_url ? (
                          <img
                            src={imgUrl(client.logo_url)}
                            alt={client.logoName}
                            style={s.clientLogo}
                          />
                        ) : (
                          <div style={s.clientLogoPlaceholder}>
                            {client.logo_name?.charAt(0).toUpperCase() || "?"}
                          </div>
                        )}

                        {/* ✅ UPDATED NAME DISPLAY */}
                        <p style={s.clientName}>{client.logo_name || "—"}</p>

                        <span style={s.idBadge}>ID: {client.logo_id}</span>

                        <div
                          style={{
                            display: "flex",
                            gap: "6px",
                            marginTop: "4px",
                          }}
                        >
                          <button
                            style={s.btnEditSm}
                            onClick={() => this.openEditClient(client)}
                          >
                            Edit
                          </button>
                          <button
                            style={s.btnDeleteSm}
                            onClick={() => this.deleteClient(client.logo_id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Client Form */}
            {clientView === "form" && (
              <div>
                <div className="rsp-form-top" style={s.formTopBar}>
                  <button className="rsp-btn-back" style={s.btnBack} onClick={this.goToClientList}>
                    Back to List
                  </button>
                  <h3 style={s.formTitle}>
                    {editingClient
                      ? `Edit Client — ${editingClient.logo_name}`
                      : "Add New Client"}
                  </h3>
                </div>

                <div className="card">
                  <div className="rsp-form-group" style={s.formGroup}>
                    <label style={s.label}>Client Logo *</label>
                      {/* <p>here = {editingClient.logo_url}</p> */}
                    {editingClient && editingClient.logo_url ? (
                      // ✅ Show new image preview (after user selects a new file)
                      // <p>{editingClient.imageUrl}</p>
                      <img
                        src={imgUrl(editingClient.logo_url)}
                        alt="current"
                        style={s.clientLogoPreview}
                      />
                    ) :  clientForm.imagePreview ? (
                      // ✅ Show existing image when editing
                     
                          <img
                        src={clientForm.imagePreview}
                        alt="preview"
                        style={s.clientLogoPreview}
                      />
                    ) : (
                      // ✅ Show upload input when no image
                      <input type="file" onChange={this.handleClientImage} />
                    )}
                  </div>

                  {/* ✅ NEW INPUT */}
                  <div className="rsp-form-group" style={s.formGroup}>
                    <label style={s.label}>Logo Name *</label>
                    <input
                      type="text"
                      value={clientForm.logo_name}
                      onChange={(e) =>
                        this.updateClientForm("logo_name", e.target.value)
                      }
                      placeholder="Enter client logo name"
                      className="rsp-input" style={s.input}
                    />
                  </div>

                  <div className="rsp-form-actions" style={s.formActions}>
                    <button className="rsp-btn-back" style={s.btnBack} onClick={this.goToClientList}>
                      Cancel
                    </button>
                    <button
                      className="rsp-btn-save" style={s.btnSave}
                      onClick={this.saveClient}
                      disabled={clientForm.saving}
                    >
                      {clientForm.saving
                        ? "Saving..."
                        : editingClient
                          ? `Update ${clientForm.logo_name || "Client"}`
                          : `Save ${clientForm.logo_name || "Client"}`}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
}

const s = {
  success: {
    background: "#e8f5e9",
    color: "#2e7d32",
    border: "1px solid #c8e6c9",
    borderRadius: "8px",
    padding: "12px 16px",
    marginBottom: "16px",
    fontSize: "14px",
  },
  error: {
    background: "#ffebee",
    color: "#c62828",
    border: "1px solid #ffcdd2",
    borderRadius: "8px",
    padding: "12px 16px",
    marginBottom: "16px",
    fontSize: "14px",
  },
  tabRow: {
    display: "flex",
    gap: "10px",
    borderBottom: "2px solid #e8e8e8",
    marginBottom: "20px",
  },
  tab: {
    padding: "10px 20px",
    background: "none",
    border: "none",
    borderBottom: "3px solid transparent",
    fontSize: "14px",
    color: "#888",
    cursor: "pointer",
    marginBottom: "-2px",
    fontWeight: "500",
  },
  tabActive: {
    padding: "10px 20px",
    background: "none",
    border: "none",
    borderBottom: "3px solid #e8a020",
    fontSize: "14px",
    color: "#e8a020",
    cursor: "pointer",
    marginBottom: "-2px",
    fontWeight: "600",
  },
  listTopBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
  },
  countText: { fontSize: "14px", color: "#888" },
  listCard: {
    background: "white",
    borderRadius: "10px",
    padding: "18px 20px",
    border: "1px solid #e8e8e8",
    marginBottom: "12px",
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  itemTitle: { fontSize: "15px", color: "#1a1a2e" },
  idBadge: {
    background: "#f0f2f5",
    color: "#888",
    border: "1px solid #e0e0e0",
    borderRadius: "10px",
    padding: "2px 8px",
    fontSize: "11px",
  },
  dateText: { fontSize: "11px", color: "#bbb", marginTop: "4px" },
  emptyBox: {
    background: "white",
    borderRadius: "10px",
    padding: "48px 20px",
    border: "1px solid #e8e8e8",
    textAlign: "center",
  },
  emptyText: { color: "#aaa", fontSize: "14px" },
  thumbImgWide: {
    width: "120px",
    height: "60px",
    objectFit: "cover",
    borderRadius: "6px",
    border: "1px solid #e8e8e8",
    flexShrink: 0,
  },
  thumbPlaceholder: {
    width: "120px",
    height: "60px",
    background: "#f0f2f5",
    borderRadius: "6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "11px",
    color: "#aaa",
    flexShrink: 0,
  },
  actionBtns: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    flexShrink: 0,
  },
  btnEdit: {
    background: "#e3f2fd",
    color: "#1565c0",
    border: "1px solid #bbdefb",
    padding: "6px 14px",
    borderRadius: "6px",
    fontSize: "12px",
    cursor: "pointer",
    fontWeight: "600",
    minWidth: "64px",
  },
  btnDelete: {
    background: "#ffebee",
    color: "#c62828",
    border: "1px solid #ffcdd2",
    padding: "6px 14px",
    borderRadius: "6px",
    fontSize: "12px",
    cursor: "pointer",
    fontWeight: "600",
    minWidth: "64px",
  },
  btnEditSm: {
    background: "#e3f2fd",
    color: "#1565c0",
    border: "1px solid #bbdefb",
    padding: "4px 10px",
    borderRadius: "5px",
    fontSize: "11px",
    cursor: "pointer",
    fontWeight: "600",
    flex: 1,
  },
  btnDeleteSm: {
    background: "#ffebee",
    color: "#c62828",
    border: "1px solid #ffcdd2",
    padding: "4px 10px",
    borderRadius: "5px",
    fontSize: "11px",
    cursor: "pointer",
    fontWeight: "600",
    flex: 1,
  },
  clientGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: "14px",
  },
  clientCard: {
    background: "white",
    borderRadius: "10px",
    padding: "16px 12px",
    border: "1px solid #e8e8e8",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "6px",
  },
  clientLogo: { width: "80px", height: "50px", objectFit: "contain" },
  clientLogoPlaceholder: {
    width: "80px",
    height: "50px",
    background: "#f0f2f5",
    borderRadius: "6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "20px",
    fontWeight: "700",
    color: "#888",
  },
  clientName: { fontSize: "12px", color: "#333", fontWeight: "600" },
  clientLogoPreview: {
    width: "120px",
    height: "80px",
    objectFit: "contain",
    border: "1px solid #e8e8e8",
    borderRadius: "8px",
    background: "#fafafa",
  },
  formTopBar: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    marginBottom: "16px",
  },
  formTitle: { fontSize: "16px", color: "#1a1a2e", margin: 0 },
  formActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    borderTop: "1px solid #f0f0f0",
    paddingTop: "16px",
  },
  formGroup: { marginBottom: "16px" },
  label: {
    display: "block",
    fontSize: "13px",
    color: "#555",
    fontWeight: "600",
    marginBottom: "6px",
  },
  input: {
    width: "100%",
    padding: "10px 14px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    fontSize: "14px",
    outline: "none",
    fontFamily: "Arial, sans-serif",
  },
  uploadBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    border: "2px dashed #ddd",
    borderRadius: "10px",
    padding: "36px 20px",
    cursor: "pointer",
    background: "#fafafa",
    textAlign: "center",
  },
  uploadText: { fontSize: "14px", color: "#555", marginBottom: "4px" },
  uploadHint: { fontSize: "11px", color: "#aaa" },
  previewBox: {
    position: "relative",
    borderRadius: "10px",
    overflow: "hidden",
    border: "1px solid #e8e8e8",
  },
  previewImg: {
    width: "100%",
    height: "200px",
    objectFit: "cover",
    display: "block",
  },
  overlay: {
    position: "absolute",
    bottom: "10px",
    right: "10px",
    display: "flex",
    gap: "8px",
  },
  btnRed: {
    background: "#e53935",
    color: "#fff",
    border: "none",
    padding: "7px 14px",
    borderRadius: "6px",
    fontSize: "12px",
    cursor: "pointer",
    fontWeight: "600",
  },
  btnDark: {
    background: "#1a1a2e",
    color: "#fff",
    border: "none",
    padding: "7px 14px",
    borderRadius: "6px",
    fontSize: "12px",
    cursor: "pointer",
    fontWeight: "600",
  },
  btnSave: {
    background: "#e8a020",
    color: "#fff",
    border: "none",
    padding: "10px 20px",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
  },
  btnBack: {
    background: "#f0f2f5",
    color: "#333",
    border: "1px solid #ddd",
    padding: "9px 16px",
    borderRadius: "8px",
    fontSize: "13px",
    cursor: "pointer",
    fontWeight: "500",
  },
};

export default Clients;
